import { v4 as uuidv4 } from 'uuid'
import { AxiosService } from './axios.service'
import { StorageService } from './storage.service'
import { dateHelper } from '../helpers/date.helper'

/**
 * Defines the API methods for the shopping cart
 */
export class CartService {
	static baseURL = 'carts'

	/**
	 * Creates a shopping cart on the MuviHub API and stores it, along with the
	 * session used to create it, in localStorage
	 * @param {Date|string} [returnDate] The return date, when the user is
	 * buying a round trip - omitted entirely for one-way purchases
	 * @param {{orderId: string, orderNumber?: number, ticketId: string}} [rebooking] The original
	 * order/ticket being rebooked - omitted for a regular purchase. `orderNumber` is only needed
	 * for a legacy-only order (bought before the native cart migration - `orderId` is then a
	 * synthetic guid with no row in the Muvi Hub `orders` table); it enables the server-side
	 * fallback to the legacy system, same pattern as `ClientTicketService.getOrder`. The
	 * created cart comes back with a `rebookingContext` (original ticket
	 * value + whether the difference is a charge/refund) when this is set.
	 * @return {Promise<any>} The created cart, including its id
	 */
	static createCart(returnDate, rebooking) {
		const params = {
			currency: 'BRL',
			sessionId: `ses_${StorageService.useSessionId.get()}`
		}

		if (returnDate) {
			params.returnDate = dateHelper.formatToYYYYMMDD(returnDate)
		}

		if (rebooking && rebooking.orderId && rebooking.ticketId) {
			params.rebooking = {
				originalOrderId: rebooking.orderId,
				originalTicketId: rebooking.ticketId
			}

			if (rebooking.orderNumber) {
				params.rebooking.originalOrderNumber = rebooking.orderNumber
			}
		}

		return AxiosService.postMuviHub(CartService.baseURL, params).then(({ data }) => {
			const cart = data.data

			StorageService.cart.set({ ...cart, sessionId: params.sessionId })
			return cart
		})
	}

	/**
	 * The id of the cart currently in progress, if any
	 * @return {string|undefined} The cart id
	 */
	static getCurrentCartId() {
		const cart = StorageService.cart.get()

		return cart && cart.id
	}

	/**
	 * Applies a discount coupon to the current cart
	 * @param {string} code The coupon code
	 * @param {string} cpf The buyer's CPF, required by the API to validate the coupon
	 * @return {Promise<any>} { success, message, data: <cart summary: coupon, totals, poltronas, ...> }
	 */
	static applyCoupon(code, cpf) {
		const cart = StorageService.cart.get()

		return AxiosService.postMuviHub(
			`${CartService.baseURL}/${cart && cart.id}/coupon`,
			{ code, cpf }
		).then(({ data }) => data)
	}

	/**
	 * Checks out the current cart, charging the given payment instrument
	 * @param {object} payload { payment, buyer, passengers, context }
	 * @return {Promise<any>} the checkout result
	 */
	static checkout(payload) {
		const cart = StorageService.cart.get()

		return AxiosService.postMuviHub(
			`${CartService.baseURL}/${cart && cart.id}/checkout`,
			payload,
			{ headers: { 'X-Idempotency-Key': uuidv4() } }
		).then(({ data }) => data)
	}

	/**
	 * Checks out a rebooking cart: charges/refunds only the difference against
	 * the original ticket and links the new order to it (server-side) -
	 * `RebookingCheckoutHandler` (Muvi Hub) + `RebookingTicketCommand` (Moveflex)
	 * @param {string} cartId The new-trip cart id
	 * @param {object} payload { payment, buyer, passengers, context, rebooking: { originalOrderId, originalTicketId } }
	 * @return {Promise<any>} the checkout result - same shape as `checkout()`
	 */
	static rebook(cartId, payload) {
		return AxiosService.postMuviHub(
			`${CartService.baseURL}/${cartId}/rebook`,
			payload,
			{ headers: { 'X-Idempotency-Key': uuidv4() } }
		).then(({ data }) => data)
	}
}
